import type {Comment as TComment} from '@/apiTypes/comment';
import type {EventListSummary} from '@/apiTypes/eventList';
import {AdminBar} from '@/components/adminBar/adminBar';
import {SuperadminBar} from '@/components/adminBar/superadminBar';
import {CommentsCard} from '@/components/eventComments/eventComments';
import {EventHero} from '@/components/eventHero/eventHero';
import {
  AboutSection,
  HostedByCard,
  PrerequisitesCard,
} from '@/components/eventInfo/eventInfo';
import {LocationCard} from '@/components/eventLocation/eventLocation';
import {useAuth} from '@/components/AuthProvider';
import {AboutEvent} from '@/components/eventPage/aboutEvent/aboutEvent';
import {RegistrationHandoffModal} from '@/components/eventPage/registrationHandoffModal/registrationHandoffModal';
import {RegistrationPanel} from '@/components/eventPage/registrationPanel/registrationPanel';
import {RegistrationFlowModal} from '@/components/eventRegistration/registrationFlowModal';
import {RegistrationInfoModal} from '@/components/eventRegistration/registrationInfoModal';
import {ParticipantsCard} from '@/components/eventParticipants/eventParticipants';
import {RoutesCard} from '@/components/eventRoutes/eventRoutes';
import {EventTags} from '@/components/eventPage/eventTags/eventTags';
import {WayraSaysCard} from '@/components/eventPage/wayraSaysCard/wayraSaysCard';
import {Button} from '@/components/ui/button';
import {Card, CardContent} from '@/components/ui/card';
import {Footer} from '@/components/ui/footer';
import {Navbar} from '@/components/ui/navbar';
import {toastManager} from '@/components/ui/toast';
import {useAnalytics} from '@/hooks/useAnalytics';
import {useIsMobile} from '@/hooks/useIsMobile';
import {
  EventRegistrationProvider,
  useEventRegistrationActor,
  useEventRegistrationState,
} from '@/machines/eventRegistrationMachine/eventRegistrationContext';
import {eventRegistrationMachine} from '@/machines/eventRegistrationMachine/eventRegistrationMachine';
import {getEventComments} from '@/server/comments';
import {getEventPageData} from '@/server/event';
import {getAllEventParticipants} from '@/server/registration';
import {getEventListMembership, getUserEventLists} from '@/server/savedEvent';
import {buildEventSchema} from '@/lib/seo/eventSchema';
import {formatEventDate} from '@/utils/displayFormatUtils';
import {
  getRegistrationPlatform,
  getSourceAttribution,
} from '@/utils/eventDisplayUtils';
import {
  Link,
  createFileRoute,
  getRouteApi,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import {motion} from 'motion/react';
import {ReactNode, useEffect, useMemo, useRef, useState} from 'react';
import {useActorRef, useSelector} from '@xstate/react';
import type {InputFrom} from 'xstate';
import {
  Event,
  AttendanceStatus,
  EventRegistrationStatus,
  EventParticipants,
  MembershipStatus,
} from '@/apiTypes/event';
import {PrerequisiteStatus} from '@/apiTypes/registration';
import {MobileRegistration} from '@/components/eventPage/mobileRegistration/MobileRegistration';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://wayra.cc';

/**
 * sessionStorage key used to stash a pending track intent across the
 * sign-in redirect. Retains the legacy attendance key so users mid-flight
 * during deploy aren't stranded.
 */
const PENDING_TRACK_KEY = 'pending_attendance';

export type EventLoaderData = {
  event: Event;
  shareDescription: string | null;
  isAdmin: boolean;
  isClaimable: boolean;
  isSuperAdmin: boolean;
  registrationStatus: EventRegistrationStatus;
  participants: EventParticipants;
  attendanceStatus: AttendanceStatus;
  prerequisiteStatus: PrerequisiteStatus;
  membershipStatus: MembershipStatus;
  lists: EventListSummary[];
  memberListIds: string[];
};

export const Route = createFileRoute('/event/$eventId/')({
  head: ({params, loaderData}) => {
    if (!loaderData) return {};

    const {event, shareDescription} = loaderData;
    const ogImageUrl = `${SITE_URL}/api/og/event/${params.eventId}`;

    const dateString = event.startDate
      ? formatEventDate(event.startDate, event.endDate ?? null)
      : null;
    const descriptionParts = [
      dateString,
      event.location,
      event.organizer ? `Hosted by ${event.organizer.name}` : null,
    ].filter(Boolean);
    const description =
      descriptionParts.length > 0
        ? `${event.name} — ${descriptionParts.join('. ')}. Discover cycling events on Wayra.`
        : `${event.name}. Discover cycling events on Wayra.`;

    const year = event.startDate
      ? new Date(event.startDate).getUTCFullYear()
      : null;
    const organizerName = event.organizer?.name;
    const titleParts = [
      event.name,
      year,
      organizerName ? `· ${organizerName}` : null,
    ]
      .filter(Boolean)
      .join(' ');
    const title = `${titleParts} | Wayra`;

    const schema = buildEventSchema(event, {
      siteUrl: SITE_URL,
      description,
    });
    const schemaJson = JSON.stringify(schema).replace(/<\//g, '<\\/');

    return {
      meta: [
        {title},
        {name: 'description', content: description},
        {property: 'og:title', content: title},
        {property: 'og:description', content: description},
        {property: 'og:image', content: ogImageUrl},
        {property: 'og:image:width', content: '1200'},
        {property: 'og:image:height', content: '630'},
        {property: 'og:image:alt', content: event.name},
        {property: 'og:url', content: `${SITE_URL}/event/${params.eventId}`},
        {name: 'twitter:title', content: title},
        {name: 'twitter:description', content: description},
        {name: 'twitter:image', content: ogImageUrl},
        {name: 'twitter:image:alt', content: event.name},
      ],
      links: [
        {rel: 'canonical', href: `${SITE_URL}/event/${params.eventId}`},
      ],
      scripts: [
        {
          type: 'application/ld+json',
          children: schemaJson,
        },
      ],
    };
  },
  component: EventPage,
  validateSearch: (search: Record<string, unknown>) => ({
    ...(search.unauthorized === true && {unauthorized: true}),
    ...(typeof search.pendingRoute === 'string' && {
      pendingRoute: search.pendingRoute,
    }),
    ...(typeof search.pendingCategory === 'string' && {
      pendingCategory: search.pendingCategory,
    }),
  }),
  loader: async ({params, context}): Promise<EventLoaderData> => {
    const eventData = await getEventPageData({data: params.eventId});

    const profileId = context.profile?.id;

    // Fire list fetches in parallel — don't block page render on failure
    const [lists, memberListIds] = profileId
      ? await Promise.all([
          getUserEventLists().catch((err) => {
            console.error('Failed to fetch event lists:', err);
            return [] as EventListSummary[];
          }),
          getEventListMembership({data: params.eventId}).catch((err) => {
            console.error('Failed to fetch list membership:', err);
            return [] as string[];
          }),
        ])
      : [[] as EventListSummary[], [] as string[]];

    const isSuperAdmin = context.profile?.level === 'superadmin';
    const isAdmin = eventData.organizerAdminStatus.isAdmin;
    const hasOrganizer = Boolean(eventData.event.organizerId);
    const isClaimable =
      hasOrganizer && eventData.organizerAdminStatus.isClaimable;

    return {
      event: eventData.event,
      shareDescription: eventData.shareDescription,
      isAdmin,
      isClaimable,
      isSuperAdmin,
      registrationStatus: eventData.registrationStatus,
      participants: eventData.participants,
      attendanceStatus: eventData.attendanceStatus,
      prerequisiteStatus: eventData.prerequisiteStatus,
      membershipStatus: eventData.membershipStatus,
      lists,
      memberListIds,
    };
  },
  errorComponent: ({error}) => {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md mx-4">
          <CardContent className="px-6 text-center">
            <h1 className="text-xl font-semibold text-gray-900 mb-2 font-sans">
              Event Not Found
            </h1>
            <p className="text-gray-600 mb-4">
              {error?.message || "The event you're looking for doesn't exist."}
            </p>
            <Link to="/search">
              <Button>Browse Events</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  },
});

function EventPage() {
  const {
    event,
    isAdmin,
    isClaimable,
    isSuperAdmin,
    registrationStatus,
    participants,
    attendanceStatus,
    prerequisiteStatus,
    membershipStatus,
    lists,
    memberListIds,
  } = Route.useLoaderData();
  const {unauthorized, pendingRoute, pendingCategory} = Route.useSearch();
  const {profile} = useAuth();
  const navigate = useNavigate();
  const {featureFlags} = Route.useRouteContext();
  const commentsEnabled = featureFlags['enable-comments'];

  const [eventComments, setEventComments] = useState<{
    comments: TComment[];
    totalCount: number;
    organizerName: string | null;
  } | null>(null);

  useEffect(() => {
    if (!commentsEnabled) return;
    let cancelled = false;
    setEventComments(null);
    getEventComments({data: event.id})
      .then((result) => {
        if (!cancelled) setEventComments(result);
      })
      .catch((error) => {
        if (!cancelled) console.error('Failed to load event comments', error);
      });
    return () => {
      cancelled = true;
    };
  }, [event.id, commentsEnabled]);

  const [selectedRouteId, setSelectedRouteId] = useState(
    event.routes?.[0]?.id ?? null
  );

  const [allParticipants, setAllParticipants] = useState<
    typeof participants.participants | null
  >(null);
  const [participantsModalOpen, setParticipantsModalOpen] = useState(false);
  const [allParticipantsLoading, setAllParticipantsLoading] = useState(false);

  const handleOpenParticipants = async () => {
    setParticipantsModalOpen(true);
    if (allParticipants !== null) return;
    setAllParticipantsLoading(true);
    try {
      const all = await getAllEventParticipants({data: event.id});
      setAllParticipants(all);
    } catch (err) {
      toastManager.add({
        title:
          err instanceof Error ? err.message : 'Failed to load participants',
        type: 'error',
      });
      setParticipantsModalOpen(false);
    } finally {
      setAllParticipantsLoading(false);
    }
  };

  useEffect(() => {
    if (unauthorized) {
      toastManager.add({
        title: 'You do not have permissions to edit this event',
        type: 'error',
      });
      navigate({
        to: '/event/$eventId',
        params: {eventId: event.id},
        search: {},
        replace: true,
      });
    }
  }, [unauthorized, event.id, navigate]);

  const {track} = useAnalytics();
  useEffect(() => {
    track('Event Viewed', {
      event_id: event.id,
      event_name: event.name,
      event_type: event.eventType,
      is_external: !!event.externalId,
    });
  }, [track, event.id, event.eventType, event.externalId, event.name]);

  const isMobile = useIsMobile();

  // Pending params come from the login resumption flow — a user clicked
  // Register while unauthenticated and we encoded their selection in the
  // redirect URL. Validate them against the current event before trusting;
  // stale params are silently dropped.
  const validatedPendingRouteId =
    pendingRoute && (event.routes ?? []).some((r) => r.id === pendingRoute)
      ? pendingRoute
      : null;
  const validatedPendingCategoryId =
    pendingCategory &&
    (event.categories ?? []).some((c) => c.id === pendingCategory)
      ? pendingCategory
      : null;
  const hasPendingSelection =
    validatedPendingRouteId != null || validatedPendingCategoryId != null;

  // Auto-select only when the choice is unambiguous: exactly one route (or
  // none) and exactly one category visible for that route. Otherwise leave
  // selections null so the UI shows "none selected" placeholders. Pending
  // params from the resumption flow take precedence over auto-select.
  const autoSelectedRouteId =
    (event.routes ?? []).length === 1 ? event.routes![0].id : null;
  const initialRouteId = validatedPendingRouteId ?? autoSelectedRouteId;
  const initialVisibleCategories = initialRouteId
    ? (event.categories ?? []).filter((c) => c.routeId === initialRouteId)
    : (event.categories ?? []).filter((c) => !c.routeId);
  const autoSelectedCategoryId =
    initialVisibleCategories.length === 1
      ? initialVisibleCategories[0].id
      : null;
  const initialCategoryId =
    validatedPendingCategoryId ?? autoSelectedCategoryId;

  const machineInput: EventRegistrationMachineInput = {
    eventId: event.id,
    // Indexed (external) events use the `track` intent so the unified CTA
    // marks attendance + hands off; native events stay on `register`.
    intent: event.sourceUrl ? 'track' : 'register',
    categoryId: initialCategoryId,
    routeId: initialRouteId,
    profile,
    skipRiderFields: event.skipRiderFields,
    prerequisiteStatus,
    requiresClubMembership: event.requiresClubMembership,
    isMember: membershipStatus.isMember,
    offPlatformClaim: false,
  };

  const page = (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      <Navbar />

      {/* Hero */}
      <EventHero event={event} lists={lists} memberListIds={memberListIds}>
        {/* Admin bar — regular member bar takes priority over superadmin bar */}
        {isAdmin && <AdminBar eventId={event.id} />}
        {isSuperAdmin && !isAdmin && (
          <SuperadminBar
            to="/admin/dashboard/event/$eventId/edit"
            params={{eventId: event.id}}
          />
        )}
      </EventHero>

      {/* Main Content */}
      <motion.div
        className="max-w-5xl mx-auto px-4 py-6 pb-24 lg:pb-6 flex-1 w-full"
        initial={{opacity: 0, y: 30, filter: 'blur(4px)'}}
        animate={{opacity: 1, y: 0, filter: 'blur(0px)'}}
        transition={{
          type: 'spring',
          stiffness: 120,
          damping: 20,
          delay: 0.175,
        }}
      >
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Column - Main Content */}
          <div className="flex flex-col flex-1 gap-6 lg:pr-6 lg:border-r">
            <PrerequisitesCard
              prerequisites={prerequisiteStatus.prerequisites}
              allSatisfied={prerequisiteStatus.allSatisfied}
            />
            <HostedByCard event={event} />
            {event.editorialContent && (
              <>
                <hr />
                <WayraSaysCard content={event.editorialContent} author={event.editorialAuthor ?? null} />
              </>
            )}
            <hr />
            <AboutSection event={event} isClaimable={isClaimable} />
            <hr />
            <LocationCard event={event} />
            <hr />
            <RoutesCard
              event={event}
              selectedRouteId={selectedRouteId}
              onSelectRoute={setSelectedRouteId}
            />
            {event.tags && event.tags.length > 0 && (
              <div className="lg:hidden">
                <hr />
                <EventTags tags={event.tags} />
              </div>
            )}
            <ParticipantsCard
              participants={participants.participants}
              totalCount={participants.totalCount}
              eventId={event.id}
              eventName={event.name}
              allParticipants={allParticipants}
              allParticipantsLoading={allParticipantsLoading}
              participantsModalOpen={participantsModalOpen}
              onOpenParticipants={handleOpenParticipants}
              onCloseParticipants={() => setParticipantsModalOpen(false)}
            />

            {commentsEnabled && eventComments && (
              <CommentsCard
                eventId={event.id}
                comments={eventComments.comments}
                totalCount={eventComments.totalCount}
                isFetching={false}
                isOrganizerAdmin={isAdmin}
                registrationStatus={registrationStatus.registration}
                organizer={event.organizer}
              />
            )}
            {isMobile && <AboutEvent canClaim={isClaimable} event={event} />}
          </div>

          {/* Right Column - Sidebar (desktop only) */}
          <div className="hidden lg:block w-80 flex-shrink-0">
            <div className="lg:sticky lg:top-20 flex flex-col gap-4">
              <div className="bg-white p-6 rounded-xl border">
                <RegistrationPanel
                  event={event}
                  isTracking={attendanceStatus.isAttending}
                  isAdmin={isAdmin}
                  isMember={membershipStatus.isMember}
                  registrationStatus={registrationStatus}
                />
              </div>
              {event.tags && event.tags.length > 0 && (
                <EventTags tags={event.tags} />
              )}
            </div>
          </div>
        </div>
      </motion.div>
      {/* Mobile registration: pull-up sheet for native, fixed button for external */}
      <MobileRegistration />
      {/* TODO: Make footer fully appear on mobile, covered by registration ATM */}
      <Footer />
    </div>
  );

  // Active-intent confirmation flag: which loader value flips true when the
  // machine's submit succeeds depends on whether we registered (native) or
  // tracked (indexed). The wrapper waits on this to dispatch
  // `REGISTRATION_CONFIRMED` and exit `flow.refreshing`.
  const isConfirmed = event.sourceUrl
    ? attendanceStatus.isAttending
    : registrationStatus.isRegistered;

  if (machineInput) {
    return (
      <WithEventRegistrationMachine
        input={machineInput}
        event={event}
        isConfirmed={isConfirmed}
        resumePendingRegistration={hasPendingSelection}
      >
        {page}
      </WithEventRegistrationMachine>
    );
  }

  return page;
}

type EventRegistrationMachineInput = InputFrom<typeof eventRegistrationMachine>;

interface WithEventRegistrationMachineProps {
  input: EventRegistrationMachineInput;
  /**
   * Subset of the loader's event needed for the auto-shown handoff modal
   * analytics call. Keeping this as an explicit subset (rather than bloating
   * `machineInput` with display-only fields) keeps the actor's input shape
   * focused on machine concerns.
   */
  event: Pick<Event, 'id' | 'eventType' | 'externalId' | 'sourceUrl'>;
  /**
   * Loader-derived signal that the active intent's submission has been
   * persisted: `attendanceStatus.isAttending` for indexed (track) events,
   * `registrationStatus.isRegistered` for native (register) events. The
   * wrapper waits on this in `flow.refreshing` before dispatching
   * `REGISTRATION_CONFIRMED`, so a track success — which never flips
   * `isRegistered` — still exits refreshing cleanly.
   */
  isConfirmed: boolean;
  /**
   * When true, the route was opened with `pendingRoute`/`pendingCategory`
   * search params carried over from a pre-login Register click. Mount-time
   * effect auto-dispatches `REGISTER` so the machine either submits (if
   * profile + prereqs are complete) or opens the appropriate modal, then
   * strips the params from the URL so a refresh does not re-fire.
   */
  resumePendingRegistration: boolean;
  children: ReactNode;
}

/**
 * Mounts the `eventRegistrationMachine` actor with side-effect actions wired
 * to the router/toast and exposes it through `EventRegistrationProvider`.
 * Also renders machine-driven modals (profile completion, etc.) at the page
 * level so they don't depend on the sidebar being mounted.
 *
 * Orchestrates the post-submit handoff: when the machine is in `refreshing`
 * and the loader's `isConfirmed` flips true, we dispatch
 * `REGISTRATION_CONFIRMED` so the machine exits the optimistic "registered"
 * state only after the server truth has actually propagated.
 */
function WithEventRegistrationMachine({
  input,
  event,
  isConfirmed,
  resumePendingRegistration,
  children,
}: WithEventRegistrationMachineProps) {
  const router = useRouter();
  const navigate = useNavigate();
  const {track} = useAnalytics();

  const providedMachine = useMemo(
    () =>
      eventRegistrationMachine.provide({
        actions: {
          notifySuccess: ({context}) => {
            const title =
              context.intent === 'track'
                ? 'Added to your planned events'
                : "You're Registered";
            toastManager.add({title, type: 'success'});
          },
          triggerRefresh: () => {
            router.invalidate();
          },
          onRegistrationFailed: (_, {error}) => {
            const message =
              error instanceof Error ? error.message : 'Registration failed';
            toastManager.add({title: message, type: 'error'});
          },
          stashPendingTrack: ({context}) => {
            sessionStorage.setItem(PENDING_TRACK_KEY, context.eventId);
          },
          notifyMembersOnlyBlocked: () => {
            const organizerName = event.organizer?.name;
            toastManager.add({
              title: organizerName
                ? `You'll need to be a ${organizerName} member to register.`
                : 'Membership is required to register for this event.',
              type: 'info',
            });
          },
        },
      }),
    [router, event.organizer?.name]
  );

  const actor = useActorRef(providedMachine, {input});
  const isRefreshing = useSelector(actor, (s) =>
    s.matches({flow: 'refreshing'})
  );
  const needsAuth = useSelector(actor, (s) => s.matches({flow: 'needsAuth'}));

  // True only during the brief window where the machine has just transitioned
  // from `submitting → refreshing` for a track intent and the handoff modal
  // has been auto-opened by the machine's `OPEN_HANDOFF` raise. The effect
  // below fires a single 'Handoff Modal Shown' analytics event with
  // `trigger: 'auto'` while this is true; manual re-opens are tracked from
  // `<TrackedConfirmation>` instead so the two paths are not double-counted.
  const justAutoOpenedHandoff = useSelector(
    actor,
    (s) =>
      s.context.intent === 'track' &&
      s.matches({handoffModal: 'open'}) &&
      s.matches({flow: 'refreshing'})
  );

  useEffect(() => {
    if (isRefreshing && isConfirmed) {
      actor.send({type: 'REGISTRATION_CONFIRMED'});
    }
  }, [isRefreshing, isConfirmed, actor]);

  useEffect(() => {
    if (!justAutoOpenedHandoff) return;
    const platform =
      getRegistrationPlatform(event.externalId) ??
      getSourceAttribution(event.sourceUrl)?.name ??
      null;
    track('Handoff Modal Shown', {
      event_id: event.id,
      event_type: event.eventType,
      platform,
      trigger: 'auto',
    });
  }, [
    justAutoOpenedHandoff,
    event.id,
    event.eventType,
    event.externalId,
    event.sourceUrl,
    track,
  ]);

  // Machine entered `needsAuth` (unauthenticated user clicked the track CTA).
  // Send them to /login with a redirect back to this event so the post-login
  // mount-time replay below can dispatch AUTH_RESUMED.
  useEffect(() => {
    if (!needsAuth) return;
    navigate({
      to: '/login',
      search: {redirect: `/event/${input.eventId}`},
    });
  }, [needsAuth, navigate, input.eventId]);

  // After sign-in we land back on this page with the pending track intent
  // stashed in sessionStorage. The actor remounted in `flow.idle`, so replay
  // by redispatching `TRACK` — the now-authenticated user falls through to
  // `submitting` directly. Ref-guarded so StrictMode's double-invoke can't
  // fire twice.
  const hasReplayedAuthRef = useRef(false);
  useEffect(() => {
    if (hasReplayedAuthRef.current) return;
    if (input.profile == null) return;
    hasReplayedAuthRef.current = true;

    const stored = sessionStorage.getItem(PENDING_TRACK_KEY);
    if (stored == null) return;

    sessionStorage.removeItem(PENDING_TRACK_KEY);
    if (stored === input.eventId) {
      actor.send({type: 'TRACK'});
    }
  }, [actor, input.eventId, input.profile]);

  // Resume a pending registration carried over from a pre-login click.
  // Guarded by a ref so StrictMode's double-invoke and any re-render that
  // changes the dep array cannot fire this twice — which would re-dispatch
  // REGISTER after the user cancelled or hit the server a second time.
  const hasResumedRef = useRef(false);
  useEffect(() => {
    if (hasResumedRef.current) return;
    if (!resumePendingRegistration) return;
    hasResumedRef.current = true;

    // Strip the pending params regardless of what we do next — they have
    // already been consumed (seeded into machine input at mount).
    navigate({
      to: '.',
      search: (prev) => ({
        ...prev,
        pendingRoute: undefined,
        pendingCategory: undefined,
      }),
      replace: true,
    });

    // The machine's REGISTER guards drop the event when the user can't
    // actually register (members-only block); dispatching is safe.
    if (input.profile && !isConfirmed) {
      actor.send({type: 'REGISTER'});
    }
  }, [resumePendingRegistration, navigate, actor, input.profile, isConfirmed]);

  return (
    <EventRegistrationProvider actor={actor}>
      {children}
      <EventRegistrationModals />
    </EventRegistrationProvider>
  );
}

const eventRouteApi = getRouteApi('/event/$eventId/');

/**
 * Renders modals driven by the `eventRegistrationMachine` state. Reads
 * `profile`, `skipRiderFields`, and `prerequisiteStatus` from machine
 * context (seeded at mount by `WithEventRegistrationMachine`). Event-level
 * display data (name, category name, route name, price) comes from the
 * route's loader via `getRouteApi`.
 */
function EventRegistrationModals() {
  const state = useEventRegistrationState();
  const actor = useEventRegistrationActor()!;
  const {event} = eventRouteApi.useLoaderData();

  // The post-track handoff modal sits on a parallel state region and can be
  // open simultaneously with the flow-driven modals — render it as a sibling
  // so its visibility doesn't fight the early-return chain below.
  const handoffModal = event.sourceUrl ? (
    <RegistrationHandoffModal
      event={{
        id: event.id,
        name: event.name,
        sourceUrl: event.sourceUrl,
        externalId: event.externalId,
        eventType: event.eventType,
      }}
    />
  ) : null;

  if (state.matches({flow: 'needsProfile'}) && state.context.profile) {
    return (
      <>
        <RegistrationInfoModal
          profile={state.context.profile}
          skipRiderFields={state.context.skipRiderFields}
          onComplete={() => actor.send({type: 'PROFILE_COMPLETED'})}
          onClose={() => actor.send({type: 'CANCEL_PROFILE'})}
        />
        {handoffModal}
      </>
    );
  }

  if (state.matches({flow: 'needsPrerequisiteConfirm'}) && state.context.profile) {
    const {categoryId, routeId, prerequisiteStatus} = state.context;
    const category = (event.categories ?? []).find((c) => c.id === categoryId);
    const route = routeId
      ? (event.routes ?? []).find((r) => r.id === routeId)
      : undefined;
    const lowestPrice = category?.prices?.reduce(
      (min, p) => (p.amount < min ? p.amount : min),
      Number.POSITIVE_INFINITY
    );
    // Omit the Price row entirely for free events (no price tiers, or all
    // tiers $0) — surfacing "$0.00" in the confirmation is noise.
    const categoryPrice =
      lowestPrice != null &&
      lowestPrice !== Number.POSITIVE_INFINITY &&
      lowestPrice > 0
        ? lowestPrice
        : undefined;
    const unsatisfiedPrerequisites = prerequisiteStatus.prerequisites.filter(
      (p) => p.satisfied === false
    );

    // Only surface route/category in the confirmation summary when the user
    // actually had options to choose from. For events with a single route or
    // a single visible category the selection was implicit, and showing it
    // is noise.
    const userPickedRoute = (event.routes ?? []).length > 1;
    const visibleCategoriesForSelection = routeId
      ? (event.categories ?? []).filter((c) => c.routeId === routeId)
      : (event.categories ?? []).filter((c) => !c.routeId);
    const userPickedCategory = visibleCategoriesForSelection.length > 1;

    return (
      <>
        <RegistrationFlowModal
          profile={state.context.profile}
          skipRiderFields={state.context.skipRiderFields}
          eventName={event.name}
          categoryName={userPickedCategory ? category?.name : undefined}
          routeName={userPickedRoute ? route?.name : undefined}
          categoryPrice={categoryPrice}
          unsatisfiedPrerequisites={unsatisfiedPrerequisites}
          onConfirm={() => actor.send({type: 'PREREQUISITES_CONFIRMED'})}
          onClose={() => actor.send({type: 'CANCEL_PREREQUISITES'})}
        />
        {handoffModal}
      </>
    );
  }

  return handoffModal;
}
